import { ExtendedWalletClient } from '@sdk/auth/types';
import ERC20PermitArb from '@shared/src/lib/abis/ERC20PermitArb';
import { Client, createPublicClient, Hex, http, parseSignature, WalletClient } from 'viem';
import { readContract } from '@shared/src/components/utils/blockchain';

interface PermitSignature {
  v: number;
  r: string;
  s: string;
}

/**
 * Sign an ERC20 permit to approve token spending without a transaction
 * Updated for ethers v6
 */
export default async function signPermit({
  spender,
  deadline,
  chain,
  walletClient,
  tokenAddress,
  owner,
  value,
}: {
  spender: Hex;
  deadline: bigint;
  chain: any;
  walletClient: ExtendedWalletClient;
  tokenAddress: Hex;
  owner: Hex;
  value: number | bigint;
}): Promise<PermitSignature> {
  // Get the token name and nonce using viem publicClient
  let version = '1';
  // You must provide a publicClient instance or fetch via walletClient
  try {
    version = await readContract({
      walletClient,
      chain,
      args: {
        abi: ERC20PermitArb,
        address: tokenAddress,
        functionName: 'version',
        args: [],
      },
    });
  } catch (e) {
    console.log('version error', e);
  }

  const name = await readContract({
    walletClient,
    chain,
    args: {
      address: tokenAddress,
      abi: [
        {
          name: 'name',
          type: 'function',
          stateMutability: 'view',
          inputs: [],
          outputs: [{ type: 'string' }],
        },
      ],
      functionName: 'name',
    },
  });
  const nonce = await readContract({
    walletClient,
    chain,
    args: {
      address: tokenAddress,
      abi: [
        {
          name: 'nonces',
          type: 'function',
          stateMutability: 'view',
          inputs: [{ name: 'owner', type: 'address' }],
          outputs: [{ type: 'uint256' }],
        },
      ],
      functionName: 'nonces',
      args: [owner],
    },
  });

  const domain = {
    name,
    version,
    chainId: chain.id,
    verifyingContract: tokenAddress,
  };
  const types = {
    Permit: [
      { name: 'owner', type: 'address' },
      { name: 'spender', type: 'address' },
      { name: 'value', type: 'uint256' },
      { name: 'nonce', type: 'uint256' },
      { name: 'deadline', type: 'uint256' },
    ],
  } as const;
  const permitData = {
    owner,
    spender,
    value: BigInt(value),
    nonce: nonce,
    deadline,
  };
  if (!walletClient.account) {
    throw new Error('Wallet client account is not set, signing permit');
  }
  const signature = await walletClient.signTypedData({
    account: walletClient.account,
    domain,
    types,
    primaryType: 'Permit',
    message: permitData,
  });
  const sig = parseSignature(signature);
  return {
    v: Number(sig.v || sig.yParity),
    r: sig.r,
    s: sig.s,
  };
}
